import { createSlice, createAsyncThunk, } from '@reduxjs/toolkit';
import { getApiClient } from '../../utils/apiClient';
// Async thunks
const loadTimerState = createAsyncThunk('timer/loadTimerState', async ({ dateDigit }) => {
    const response = await getApiClient()({
        url: '/api/timer-state',
        method: 'get',
        data: { dateDigit },
    });
    return response.data;
});
const startTimerState = createAsyncThunk('timer/startTimerState', async ({ sessionStartDateDigit, stage, taskId, allSeconds, secondsAtStart, pomodoroCnt, nowMinutes, onError, }) => {
    try {
        const response = await getApiClient()({
            url: '/api/timer-state-start',
            method: 'post',
            data: {
                sessionStartDateDigit,
                stage,
                taskId,
                allSeconds,
                secondsAtStart,
                pomodoroCnt,
                nowMinutes,
            },
            retryOnNetworkError: true,
        });
        return response.data;
    }
    catch (error) {
        const apiError = error;
        onError(apiError);
    }
});
const finishTimerState = createAsyncThunk('timer/finishTimerState', async ({ thisStage, nextStage, allSeconds, pomodoroCnt, nowMinutes, taskId, onError, }) => {
    try {
        const response = await getApiClient()({
            url: '/api/timer-state-finish',
            method: 'post',
            data: {
                thisStage,
                nextStage,
                allSeconds,
                pomodoroCnt,
                nowMinutes,
                taskId,
            },
            retryOnNetworkError: true,
        });
        return response.data;
    }
    catch (error) {
        const apiError = error;
        onError(apiError);
    }
});
const pauseTimerState = createAsyncThunk('timer/pauseTimerState', async ({ secondsAtPause, nowMinutes, stage, taskId, }) => {
    const response = await getApiClient()({
        url: '/api/timer-state-pause',
        method: 'post',
        data: {
            secondsAtPause,
            nowMinutes,
            stage,
            taskId,
        },
    });
    return response.data;
});
const switchTaskTimerState = createAsyncThunk('timer/switchTaskTimerState', async ({ taskId, allSeconds, }) => {
    const response = await getApiClient()({
        url: '/api/timer-state-switch-task',
        method: 'post',
        data: {
            taskId,
            allSeconds,
        },
    });
    return response.data;
});
const updateTimerStateStage = createAsyncThunk('timer/updateTimerStateStage', async ({ stage, allSeconds, }) => {
    const response = await getApiClient()({
        url: '/api/timer-state-update-stage',
        method: 'post',
        data: {
            stage,
            allSeconds,
        },
    });
    return response.data;
});
const updatePomodoroCnt = createAsyncThunk('timer/updatePomodoroCnt', async (pomodoroCnt, { dispatch }) => {
    // Set pomodoro count to 0 before API call
    dispatch(setPomodoroCnt(pomodoroCnt));
    const response = await getApiClient()({
        url: '/api/update-pomodoro-cnt',
        method: 'post',
        data: {
            pomodoroCnt,
        },
    });
    return response.data;
});
// Helper function
const getConsumedSeconds = (allSeconds, secondsAtPause, sessionStartTS, secondsAtStart) => {
    if (allSeconds === null) {
        return 0;
    }
    if (secondsAtPause) {
        return allSeconds - secondsAtPause;
    }
    if (secondsAtStart && sessionStartTS) {
        const secondsPassedBefore = allSeconds - secondsAtStart;
        const secondsPassedNow = Math.floor((Date.now() - sessionStartTS) / 1000);
        return secondsPassedBefore + secondsPassedNow;
    }
    return 0;
};
// Initial state
const initialState = {
    stage: 0,
    consumedSeconds: 0,
    ticking: false,
    pomodoroCnt: 0,
    darkMode: false,
    timerStateLoaded: false,
    timerCue: null,
    finishedOnBackground: false,
};
// Slice
const timerSlice = createSlice({
    name: 'timer',
    initialState,
    reducers: {
        reduceSeconds: (state, action) => {
            state.consumedSeconds += action.payload.gapSeconds;
        },
        setTicking: (state, action) => {
            state.ticking = action.payload.ticking;
        },
        setStage: (state, action) => {
            state.stage = action.payload.newStage;
            state.consumedSeconds = 0;
            state.ticking = false;
        },
        setPomodoroCnt: (state, action) => {
            state.pomodoroCnt = action.payload;
        },
        setDarkMode: (state, action) => {
            state.darkMode = action.payload.darkMode;
        },
        // Handle timer cue loading (from TimerCueActions)
        loadTimerCue: (state, action) => {
            state.timerCue = action.payload.timerCue;
        },
    },
    extraReducers: (builder) => {
        // Load timer state
        builder.addCase(loadTimerState.fulfilled, (state, action) => {
            const { timerState, finishedOnBackground } = action.payload;
            state.stage = timerState.stage || 0;
            state.ticking = timerState.ticking || false;
            state.consumedSeconds = finishedOnBackground
                ? 0
                : getConsumedSeconds(timerState.allSeconds ?? null, timerState.secondsAtPause, timerState.sessionStartTS, timerState.secondsAtStart);
            state.pomodoroCnt = timerState.pomodoroCnt || 0;
            state.timerStateLoaded = true;
            state.finishedOnBackground = finishedOnBackground;
        });
        // Handle other async actions (they don't update state directly)
        builder.addCase(startTimerState.rejected, (state, action) => {
            // Handle error if needed
            console.error('Failed to start timer state:', action.error);
        });
        builder.addCase(finishTimerState.rejected, (state, action) => {
            // Handle error if needed
            console.error('Failed to finish timer state:', action.error);
        });
        builder.addCase(pauseTimerState.rejected, (state, action) => {
            // Handle error if needed
            console.error('Failed to pause timer state:', action.error);
        });
        builder.addCase(switchTaskTimerState.rejected, (state, action) => {
            // Handle error if needed
            console.error('Failed to switch task timer state:', action.error);
        });
        builder.addCase(updateTimerStateStage.rejected, (state, action) => {
            // Handle error if needed
            console.error('Failed to update timer state stage:', action.error);
        });
        builder.addCase(updatePomodoroCnt.rejected, (state, action) => {
            // Handle error if needed
            console.error('Failed to refresh pomodoro count:', action.error);
        });
    },
});
// Export individual actions and selectors
const { reduceSeconds, setTicking, setStage, setPomodoroCnt, setDarkMode, loadTimerCue, } = timerSlice.actions;
// Basic selectors
const selectTimerState = (state) => state.timer;
const selectTimerStage = (state) => state.timer.stage;
const selectTimerTicking = (state) => state.timer.ticking;
const selectTimerConsumedSeconds = (state) => state.timer.consumedSeconds;
const selectPomodoroCnt = (state) => state.timer.pomodoroCnt;
const selectDarkMode = (state) => state.timer.darkMode;
const selectTimerCue = (state) => state.timer.timerCue;
const selectTimerStateLoaded = (state) => state.timer.timerStateLoaded;
const selectFinishedOnBackground = (state) => state.timer.finishedOnBackground;
// Derived selectors
const selectIsWorkStage = (state) => state.timer.stage === 0;
const selectIsShortBreak = (state) => state.timer.stage === 1;
const selectIsLongBreak = (state) => state.timer.stage === 2;
const selectRemainingSeconds = (state) => {
    const { timer, config } = state;
    const stageDurations = [
        config.workDuration * 60,
        config.shortBreakDuration * 60,
        config.longBreakDuration * 60,
    ];
    return Math.max(0, stageDurations[timer.stage] - timer.consumedSeconds);
};
const selectProgressPercentage = (state) => {
    const { timer, config } = state;
    const stageDurations = [
        config.workDuration * 60,
        config.shortBreakDuration * 60,
        config.longBreakDuration * 60,
    ];
    const totalSeconds = stageDurations[timer.stage];
    return totalSeconds > 0
        ? (timer.consumedSeconds / totalSeconds) * 100
        : 0;
};
// Grouped exports for better organization
export const timerActions = {
    ...timerSlice.actions,
    // Sync actions
    reduceSeconds,
    setTicking,
    setStage,
    setPomodoroCnt,
    setDarkMode,
    loadTimerCue,
    // Async actions
    loadTimerState,
    startTimerState,
    finishTimerState,
    pauseTimerState,
    switchTaskTimerState,
    updateTimerStateStage,
    updatePomodoroCnt,
};
export const timerSelectors = {
    selectTimerState,
    selectTimerStage,
    selectTimerTicking,
    selectTimerConsumedSeconds,
    selectPomodoroCnt,
    selectDarkMode,
    selectTimerCue,
    selectTimerStateLoaded,
    selectFinishedOnBackground,
    selectIsWorkStage,
    selectIsShortBreak,
    selectIsLongBreak,
    selectRemainingSeconds,
    selectProgressPercentage,
};
// Export reducer
export default timerSlice.reducer;
